import {
  PVP_MATCH_STATES,
  PVP_PLAYABLE_MODES,
  PVP_RULESETS,
  type PvpAppearance,
  type PvpMatchState,
  type PvpParticipantId,
  type PvpPlayableMode,
  type PvpRuleset,
  type PvpSeat,
  type PvpTeamSlot,
  pvpIsValidSlot,
  pvpParseParticipantId,
  pvpParticipantId,
  pvpReadAppearance,
} from '@kenocyber/shared';
import { PVP_DUEL_RULESET, type PvpSnapshot, pvpDuelSeatToParticipantId } from './legacy-duel';
import { pvpCanonicalSeats } from './roster';
import { type PvpSnapshotV3, pvpDecodeSnapshotV3 } from './snapshot-v3';

/**
 * How a ROOM is written down, in both versions this build can read.
 *
 * ## Three layers, and this file is the middle one
 *
 * A duel is persisted in three nested formats and confusing them is how a
 * resumed match becomes a different match:
 *
 * 1. the SIMULATION snapshot - `PvpSnapshot` (the historic duel shape) and
 *    `PvpSnapshotV3` (the team shape). Positions, health, shots, the generator.
 * 2. the ROOM state - this file. Who holds which chair, which generation of
 *    their connection is current, who has declared ready, when each absent
 *    person dropped, and what has already been announced.
 * 3. the Durable Object's storage, which has no version of its own and simply
 *    holds one value under one key.
 *
 * The simulation snapshot is EMBEDDED in the room state rather than merged into
 * it, so the two version independently: a team match carries a v3 snapshot
 * inside a v3 room, and a duel carries the historic snapshot inside the same v3
 * room, because the duels already on disk are stored in that shape and a format
 * change nobody asked for is a format change that can only lose.
 *
 * ## Why version 3 exists at all
 *
 * Version 2 is a PAIR by construction: `seatTaken: PvpSeat[]`, `appearance` and
 * `disconnectedSince` keyed by seat, and a two-ship snapshot. Every one of those
 * says "there are two of these", and the room now runs six. Widening version 2
 * in place would leave rooms on disk that a new build reads as if they had
 * fields they do not have; a new version refuses instead, and
 * {@link pvpUpgradeRoomStateV2} states exactly what a version 2 record can and
 * cannot supply.
 *
 * ## What a room state may never contain
 *
 * No socket, no handler, no function, no cookie, no ticket, no invite code, no
 * secret, no wallet and nothing economic. What IS here is the LOGICAL
 * connection - an id the room minted and a generation counter - which is not a
 * socket: it survives a hibernation precisely because it is a string, and it is
 * what lets a room tell the tab that came back from the tab that went away.
 */

/* -------------------------------------------------------------------------- */
/*  Version 2: the historic record, unchanged                                 */
/* -------------------------------------------------------------------------- */

/**
 * The version a two-seat room was written with.
 *
 * Kept as a constant rather than a literal because it is compared in two files
 * and it is the difference between reading a record and inventing one.
 */
export const PVP_ROOM_STATE_VERSION = 2;

/**
 * Everything a two-seat room persisted, exactly as it did.
 *
 * This type is FROZEN. It describes bytes that exist in Durable Object storage
 * right now, and a field added here would be a field a reader expects and an old
 * writer never wrote.
 */
export interface PvpRoomPersistedState {
  readonly v: number;
  readonly matchId: string;
  readonly mode: PvpPlayableMode;
  readonly state: PvpMatchState;
  readonly snapshot: PvpSnapshot;
  /** Sub-tick milliseconds owed to the next step. Dropping it would lose time. */
  readonly accumulatorMs: number;
  /** The instant the room last advanced, on the SERVER clock. */
  readonly lastPumpAt: number | null;
  /** Seats that belong to an account, whether or not a socket is attached. */
  readonly seatTaken: readonly PvpSeat[];
  /** What each seat looks like, frozen when it first attached. */
  readonly appearance: ReadonlyArray<readonly [PvpSeat, PvpAppearance]>;
  /** When each absent seat dropped. This is what the grace window is measured from. */
  readonly disconnectedSince: ReadonlyArray<readonly [PvpSeat, number]>;
  /** Whether the ending has already gone out. Stops a rebuild announcing it twice. */
  readonly announced: boolean;
}

/* -------------------------------------------------------------------------- */
/*  Version 3: the record a room of any size is written with                  */
/* -------------------------------------------------------------------------- */

export const PVP_ROOM_STATE_V3_VERSION = 3;

/**
 * One chair, persisted.
 *
 * ## `claimed` and `accountId` are two different questions
 *
 * "Somebody holds this chair" and "this is who they are" came apart the moment a
 * version 2 record had to be readable: version 2 recorded `seatTaken` and never
 * recorded an account at all, because the account lived on the socket
 * attachment and nowhere else. Collapsing the two - treating a null account as
 * an empty chair - would let a rebuilt room hand a held seat to the next
 * arrival, and deriving an account from the participant id would be inventing
 * one. So a chair may be claimed by somebody the record cannot name, and the
 * room refuses to VALIDATE an occupant it does not know rather than pretending
 * it does.
 */
export interface PvpRoomChairV3 {
  readonly participantId: PvpParticipantId;
  readonly team: 'a' | 'b';
  readonly slot: number;
  /** Whether this chair belongs to somebody at all. */
  readonly claimed: boolean;
  /** Who, when the record knows. `null` is "not recorded", never "nobody". */
  readonly accountId: string | null;
  /** Bumped on every attach and every replacement. Never reused, never rewound. */
  readonly generation: number;
  /** The logical connection currently holding this chair. A string, not a socket. */
  readonly connectionId: string | null;
  readonly ready: boolean;
  /** When this chair went quiet. The grace window is measured from here. */
  readonly disconnectedSince: number | null;
  /**
   * When this player CHOSE to abandon, or null (ADR 0043).
   *
   * Distinct from `disconnectedSince`, which is a socket that went and a chair
   * being held for it. Absent from every room persisted before ADR 0043, and
   * absent reads as `null` - which is the truth about those rooms.
   */
  readonly leftAt: number | null;
  readonly appearance: PvpAppearance | null;
  /** Whether this participant's grace window has already been declared expired. */
  readonly abandonAnnounced: boolean;
  /** Whether this participant's hull has already been announced as gone. */
  readonly eliminationAnnounced: boolean;
}

/**
 * The simulation, tagged with WHICH format it is in.
 *
 * A tag rather than a sniff. The historic duel snapshot carries no version field
 * and never did, so the only honest way to tell the two apart is for the
 * container to say - which is the same rule `PvpSimulation.restore` already
 * documents, moved up one layer so the room can hold either.
 */
export type PvpRoomSimulationV3 =
  /**
   * The historic two-seat shape. **Read, never written.**
   *
   * It has no field for a combat profile and counts a cooldown in whole ticks,
   * both of which were exactly right for every duel that was ever written in
   * it - see `pvpUpgradeDuelSnapshot` for why reading it as the shipped hull is
   * exact rather than a guess.
   */
  | { readonly kind: 'duel_v2'; readonly snapshot: PvpSnapshot }
  /** What a duel is written in now: the self-describing simulation format. */
  | { readonly kind: 'duel_v3'; readonly snapshot: PvpSnapshotV3 }
  | { readonly kind: 'team_v3'; readonly snapshot: PvpSnapshotV3 };

/** Everything needed to rebuild a room of any size, and nothing else. */
export interface PvpRoomStateV3 {
  readonly v: number;
  readonly matchId: string;
  readonly mode: PvpPlayableMode;
  readonly ruleset: PvpRuleset;
  readonly state: PvpMatchState;
  /** Every chair the ruleset has, in canonical order. Never a subset. */
  readonly chairs: readonly PvpRoomChairV3[];
  readonly simulation: PvpRoomSimulationV3;
  readonly accumulatorMs: number;
  readonly lastPumpAt: number | null;
  /** When a frame last went out. Presentation cadence, and not simulated time. */
  readonly lastBroadcastAt: number;
  /** Whether the first tick has already been announced. */
  readonly startAnnounced: boolean;
  /** Whether the ending has already gone out. */
  readonly announced: boolean;
}

/* -------------------------------------------------------------------------- */
/*  Refusals                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Every way a persisted room can be unreadable. One code per rule.
 *
 * A room that cannot be read must never be half-read. Each of these ends the
 * match with no winner, which is the honest outcome - resuming from a
 * half-understood record produces a DIFFERENT match under the losing player's
 * nose, and nothing downstream could tell.
 */
export type PvpRoomStateRefusal =
  | 'not_an_object'
  | 'unknown_version'
  | 'hybrid_document'
  | 'unexpected_shape'
  | 'not_a_string'
  | 'not_an_integer'
  | 'not_a_boolean'
  | 'negative_time'
  | 'unknown_mode'
  | 'unknown_ruleset'
  | 'unknown_match_state'
  | 'chair_not_in_ruleset'
  | 'duplicate_participant'
  | 'duplicate_account'
  | 'participant_id_mismatch'
  | 'roster_incomplete'
  | 'simulation_kind_mismatch';

export class PvpRoomStateError extends RangeError {
  override readonly name = 'PvpRoomStateError';

  constructor(
    readonly code: PvpRoomStateRefusal,
    message: string,
  ) {
    super(message);
  }
}

function refuse(code: PvpRoomStateRefusal, message: string): never {
  throw new PvpRoomStateError(code, message);
}

/* -------------------------------------------------------------------------- */
/*  Strict primitives                                                         */
/* -------------------------------------------------------------------------- */

function record(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    refuse('not_an_object', `${where} is not an object`);
  }
  return value as Record<string, unknown>;
}

/**
 * Refuses an extra key AND a missing one.
 *
 * Both directions matter and for different reasons: a missing key is a field the
 * reader would silently default, and an extra key is a field the writer thought
 * meant something. Neither is a record this build understands.
 */
function requireExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
  where: string,
  /**
   * Keys this build writes but an older record may not carry.
   *
   * Allowed to be absent, never allowed to be unknown - so a field added by a
   * later version is still refused, and only the ones named here may be
   * missing. `leftAt` is the first: rooms persisted before ADR 0043 had no
   * voluntary-exit intent to record, and reading one of those as a room nobody
   * had abandoned is the truth about it rather than a default.
   */
  optional: readonly string[] = [],
): void {
  const expected = new Set(keys);
  for (const key of Object.keys(value)) {
    if (!expected.has(key)) {
      refuse('unexpected_shape', `${where} carries an unknown field: ${key}`);
    }
  }
  const mayBeAbsent = new Set(optional);
  for (const key of keys) {
    if (!(key in value) && !mayBeAbsent.has(key)) {
      refuse('unexpected_shape', `${where} is missing ${key}`);
    }
  }
}

function text(value: unknown, where: string): string {
  if (typeof value !== 'string') {
    refuse('not_a_string', `${where} is not a string`);
  }
  return value;
}

function flag(value: unknown, where: string): boolean {
  if (typeof value !== 'boolean') {
    refuse('not_a_boolean', `${where} is not a boolean`);
  }
  return value;
}

/** A safe integer, so `NaN`, `Infinity` and `1.5` are all refused by name. */
function integer(value: unknown, where: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) {
    refuse('not_an_integer', `${where} is not a safe integer`);
  }
  return value;
}

function notNegative(value: number, where: string): number {
  if (value < 0) {
    refuse('negative_time', `${where} must not be negative`);
  }
  return value;
}

function optionalInteger(value: unknown, where: string): number | null {
  return value === null ? null : integer(value, where);
}

function optionalText(value: unknown, where: string): string | null {
  return value === null ? null : text(value, where);
}

/* -------------------------------------------------------------------------- */
/*  The decoder                                                               */
/* -------------------------------------------------------------------------- */

const CHAIR_KEYS = [
  'participantId',
  'team',
  'slot',
  'claimed',
  'accountId',
  'generation',
  'connectionId',
  'ready',
  'disconnectedSince',
  'leftAt',
  'appearance',
  'abandonAnnounced',
  'eliminationAnnounced',
] as const;

/**
 * Chair fields this build writes that a record written by an older one lacks.
 *
 * Added without a version bump on purpose: the format did not CHANGE, it gained
 * a field whose absence has an exact and correct meaning. A bump would refuse
 * every room in flight when this shipped, which is a worse answer than reading
 * them as what they are - rooms in which nobody had chosen to walk out.
 */
const CHAIR_KEYS_ADDED_AFTER_V3 = ['leftAt'] as const;

const STATE_KEYS = [
  'v',
  'matchId',
  'mode',
  'ruleset',
  'state',
  'chairs',
  'simulation',
  'accumulatorMs',
  'lastPumpAt',
  'lastBroadcastAt',
  'startAnnounced',
  'announced',
] as const;

/** The fields only a version 2 record has. Their presence in a v3 is a splice. */
const V2_ONLY_KEYS = ['seatTaken', 'snapshot'] as const;

function decodeChair(value: unknown, ruleset: PvpRuleset, index: number): PvpRoomChairV3 {
  const where = `pvp room chair ${index}`;
  const raw = record(value, where);
  requireExactKeys(raw, CHAIR_KEYS, where, CHAIR_KEYS_ADDED_AFTER_V3);

  const participantId = text(raw.participantId, `${where} participantId`);
  const teamLetter = text(raw.team, `${where} team`);
  if (teamLetter !== 'a' && teamLetter !== 'b') {
    refuse('unexpected_shape', `${where} team is neither a nor b`);
  }
  const slot = integer(raw.slot, `${where} slot`);
  if (!pvpIsValidSlot(ruleset, slot)) {
    refuse('chair_not_in_ruleset', `${ruleset} has no slot ${slot}`);
  }
  /*
   * The id is DERIVED from the pair, so one that disagrees with it is not a
   * naming choice - it is two answers to "who is this", and whichever a later
   * reader trusts, the other becomes a chair somebody else can sit in.
   */
  const parsed = pvpParseParticipantId(participantId, ruleset);
  if (parsed === null || parsed.team !== teamLetter || parsed.slot !== slot) {
    refuse('participant_id_mismatch', `${participantId} is not team ${teamLetter} slot ${slot}`);
  }

  const generation = notNegative(
    integer(raw.generation, `${where} generation`),
    `${where} generation`,
  );
  const disconnectedSince = optionalInteger(raw.disconnectedSince, `${where} disconnectedSince`);
  if (disconnectedSince !== null) {
    notNegative(disconnectedSince, `${where} disconnectedSince`);
  }
  /*
   * Absent in every room persisted before ADR 0043, and absent is `null`.
   *
   * Read defensively rather than required, because storage outlives the code
   * that wrote it: a room mid-flight when this shipped must come back as a room
   * nobody had abandoned, which is exactly what it was.
   */
  const leftAt = raw.leftAt === undefined ? null : optionalInteger(raw.leftAt, `${where} leftAt`);
  if (leftAt !== null) {
    notNegative(leftAt, `${where} leftAt`);
  }
  return {
    participantId,
    team: teamLetter,
    slot,
    claimed: flag(raw.claimed, `${where} claimed`),
    accountId: optionalText(raw.accountId, `${where} accountId`),
    generation,
    connectionId: optionalText(raw.connectionId, `${where} connectionId`),
    ready: flag(raw.ready, `${where} ready`),
    disconnectedSince,
    leftAt,
    /*
     * Read back through the version gate, not trusted because it was ours.
     * Storage outlives the code that wrote it, and drawing a profile shaped for
     * a version this build does not understand is how a player ends up looking
     * at the wrong ship.
     */
    appearance: raw.appearance === null ? null : pvpReadAppearance(raw.appearance),
    abandonAnnounced: flag(raw.abandonAnnounced, `${where} abandonAnnounced`),
    eliminationAnnounced: flag(raw.eliminationAnnounced, `${where} eliminationAnnounced`),
  };
}

function decodeSimulation(value: unknown, ruleset: PvpRuleset): PvpRoomSimulationV3 {
  const raw = record(value, 'pvp room simulation');
  requireExactKeys(raw, ['kind', 'snapshot'], 'pvp room simulation');
  const kind = text(raw.kind, 'pvp room simulation kind');
  if (kind === 'duel_v2') {
    if (ruleset !== PVP_DUEL_RULESET) {
      refuse('simulation_kind_mismatch', `${ruleset} is not stored as a duel snapshot`);
    }
    /*
     * Passed through, deliberately.
     *
     * The historic snapshot has ONE validator and it is `PvpSimulation.restore`,
     * which refuses a versioned document, a wrong ship count, a health outside
     * its bounds and a projectile list longer than a match may hold. A second
     * copy of those rules here would be a second answer to the same question,
     * and the one that drifts is the one nobody is looking at.
     */
    return {
      kind: 'duel_v2',
      snapshot: record(raw.snapshot, 'pvp duel snapshot') as unknown as PvpSnapshot,
    };
  }
  if (kind === 'duel_v3') {
    if (ruleset !== PVP_DUEL_RULESET) {
      refuse('simulation_kind_mismatch', `${ruleset} is not stored as a duel snapshot`);
    }
    /*
     * Validated HERE, unlike `duel_v2`, because there is a decoder to validate
     * it with. The v3 format is self-describing and its decoder already refuses
     * a health above the hull's own ceiling, a cooldown below one negative tick,
     * a profile outside the shared representation limits and a participant id
     * that is not a chair of the ruleset the document names.
     */
    const snapshot = pvpDecodeSnapshotV3(raw.snapshot);
    if (snapshot.ruleset !== PVP_DUEL_RULESET) {
      /*
       * The container and the document have to agree. A duel record holding a
       * six-chair snapshot would restore a team match into a two-chair room, and
       * the mismatch is the only place either half can be caught.
       */
      refuse('simulation_kind_mismatch', `a duel_v3 record holds a ${snapshot.ruleset} snapshot`);
    }
    return { kind: 'duel_v3', snapshot };
  }
  if (kind === 'team_v3') {
    if (ruleset === PVP_DUEL_RULESET) {
      refuse('simulation_kind_mismatch', `${ruleset} is not stored as a team snapshot`);
    }
    const snapshot = pvpDecodeSnapshotV3(raw.snapshot);
    if (snapshot.ruleset === PVP_DUEL_RULESET) {
      refuse('simulation_kind_mismatch', `a team_v3 record holds a ${snapshot.ruleset} snapshot`);
    }
    return { kind: 'team_v3', snapshot };
  }
  refuse('simulation_kind_mismatch', `unknown pvp room simulation kind: ${kind}`);
}

/**
 * Reads a version 3 room, or refuses.
 *
 * Takes `unknown` on purpose: this is what comes back out of storage, and a
 * decoder typed to its own output has already assumed the thing it exists to
 * check. Nothing is repaired - every refusal above ends the match with no
 * winner rather than resuming a record this build only half understands.
 */
export function pvpDecodeRoomStateV3(value: unknown): PvpRoomStateV3 {
  const raw = record(value, 'pvp room state');
  const version = raw.v;
  if (version !== PVP_ROOM_STATE_V3_VERSION) {
    refuse('unknown_version', `unknown pvp room state version: ${String(version)}`);
  }
  /*
   * A version 3 record carrying a version 2 field was not written by either
   * encoder - it was spliced, and reading it would mean choosing which half to
   * believe. Checked before the exact-keys pass so the message names the
   * actual mistake rather than "unknown field".
   */
  for (const key of V2_ONLY_KEYS) {
    if (key in raw) {
      refuse('hybrid_document', `a pvp room state carrying "${key}" is not version 3`);
    }
  }
  requireExactKeys(raw, STATE_KEYS, 'pvp room state');

  const mode = text(raw.mode, 'pvp room mode');
  if (!(PVP_PLAYABLE_MODES as readonly string[]).includes(mode)) {
    refuse('unknown_mode', `a pvp room cannot be played in mode ${mode}`);
  }
  const ruleset = text(raw.ruleset, 'pvp room ruleset');
  if (!(PVP_RULESETS as readonly string[]).includes(ruleset)) {
    refuse('unknown_ruleset', `unknown pvp ruleset: ${ruleset}`);
  }
  const state = text(raw.state, 'pvp room state name');
  if (!(PVP_MATCH_STATES as readonly string[]).includes(state)) {
    refuse('unknown_match_state', `unknown pvp match state: ${state}`);
  }

  if (!Array.isArray(raw.chairs)) {
    refuse('unexpected_shape', 'pvp room chairs is not an array');
  }
  const chairs = raw.chairs.map((chair, index) => decodeChair(chair, ruleset as PvpRuleset, index));
  assertCompleteRoster(chairs, ruleset as PvpRuleset);

  const accumulatorMs = notNegative(
    integer(raw.accumulatorMs, 'pvp room accumulatorMs'),
    'pvp room accumulatorMs',
  );
  const lastPumpAt = optionalInteger(raw.lastPumpAt, 'pvp room lastPumpAt');
  return {
    v: PVP_ROOM_STATE_V3_VERSION,
    matchId: text(raw.matchId, 'pvp room matchId'),
    mode: mode as PvpPlayableMode,
    ruleset: ruleset as PvpRuleset,
    state: state as PvpMatchState,
    chairs,
    simulation: decodeSimulation(raw.simulation, ruleset as PvpRuleset),
    accumulatorMs,
    lastPumpAt: lastPumpAt === null ? null : notNegative(lastPumpAt, 'pvp room lastPumpAt'),
    lastBroadcastAt: notNegative(
      integer(raw.lastBroadcastAt, 'pvp room lastBroadcastAt'),
      'pvp room lastBroadcastAt',
    ),
    startAnnounced: flag(raw.startAnnounced, 'pvp room startAnnounced'),
    announced: flag(raw.announced, 'pvp room announced'),
  };
}

/**
 * Every chair the ruleset has, exactly once, with no account in two of them.
 *
 * The complete roster is what lets everything downstream index by identity
 * without asking whether the chair exists. A partial one would run - three
 * ships a side minus the one the record forgot - which is worse than a refusal,
 * because the missing ship is a ship somebody is sitting in.
 */
function assertCompleteRoster(chairs: readonly PvpRoomChairV3[], ruleset: PvpRuleset): void {
  const seen = new Set<PvpParticipantId>();
  const accounts = new Set<string>();
  for (const chair of chairs) {
    if (seen.has(chair.participantId)) {
      refuse('duplicate_participant', `participant ${chair.participantId} appears twice`);
    }
    seen.add(chair.participantId);
    if (chair.accountId !== null) {
      if (accounts.has(chair.accountId)) {
        /*
         * One account in two chairs is one pair of hands driving two ships.
         * Refused here as well as in the lobby, because a persisted record is
         * exactly where a rule that only lived at the door stops applying.
         */
        refuse('duplicate_account', 'one account holds two chairs in this room');
      }
      accounts.add(chair.accountId);
    }
  }
  for (const seat of pvpCanonicalSeats(ruleset)) {
    if (!seen.has(seat.participantId)) {
      refuse('roster_incomplete', `${ruleset} needs ${seat.participantId} and the record has none`);
    }
  }
}

/* -------------------------------------------------------------------------- */
/*  Version 2, upgraded rather than guessed at                                */
/* -------------------------------------------------------------------------- */

/**
 * Reads a two-seat record as a version 3 room.
 *
 * ## What version 2 supplies, and what it cannot
 *
 * It supplies everything the room needs to keep PLAYING: the ruleset is
 * `duel_1v1` by construction, seat `a` is participant `a1` and seat `b` is
 * `b1`, and readiness, appearance, drop instants, the simulation, the result,
 * the announcement flag and both clocks come across unchanged.
 *
 * It does NOT supply three things, and none of them is invented here:
 *
 * - **the account in each chair.** Version 2 never recorded one - it lived on
 *   the socket attachment, which is not part of the record. So the chair comes
 *   back `claimed` (which version 2 DID record, as `seatTaken`) with a `null`
 *   account, and the caller supplies it when the socket re-attaches or refuses
 *   to validate an occupant it cannot name. Deriving `a1`'s account from the
 *   string `a1` would be inventing an identity, which is the one thing a
 *   persistence layer must never do.
 * - **the connection generation.** Version 2 kept it in the Durable Object's
 *   memory, so it starts at zero and the first attach after the upgrade takes
 *   generation 1. That is correct rather than merely safe: every socket that
 *   could have held an earlier generation died with the process that minted it.
 * - **the logical connection.** A version 2 room is rebuilt with no chair
 *   connected, which is exactly what it was: the sockets belong to the runtime
 *   and are re-attached afterwards.
 *
 * No slot 2 and no slot 3 is invented either. `duel_1v1` has one slot a side,
 * so the upgraded roster is exactly two chairs, and a reader that wanted six
 * out of a two-seat record is a reader about to run a match four people are not
 * in.
 */
export function pvpUpgradeRoomStateV2(persisted: PvpRoomPersistedState): PvpRoomStateV3 {
  if (persisted.v !== PVP_ROOM_STATE_VERSION) {
    refuse('unknown_version', `unknown pvp room state version: ${String(persisted.v)}`);
  }
  /*
   * The three lists version 2 keyed by seat, checked before they are walked.
   *
   * Not a full re-validation of the historic format - `PvpSimulation.restore`
   * owns the snapshot and `pvpReadAppearance` owns the profiles - but enough
   * that a truncated record refuses by NAME rather than by a `TypeError` from
   * `new Set(undefined)`, which says nothing about what was actually wrong.
   */
  for (const field of ['seatTaken', 'appearance', 'disconnectedSince'] as const) {
    if (!Array.isArray(persisted[field])) {
      refuse('unexpected_shape', `a version 2 pvp room needs its ${field} list`);
    }
  }
  if (typeof persisted.matchId !== 'string') {
    refuse('not_a_string', 'a version 2 pvp room needs its match id');
  }
  const taken = new Set<PvpSeat>(persisted.seatTaken);
  const appearance = new Map<PvpSeat, PvpAppearance>(
    persisted.appearance.map(([seat, profile]) => [seat, pvpReadAppearance(profile)]),
  );
  const dropped = new Map<PvpSeat, number>(persisted.disconnectedSince);
  const chairs: PvpRoomChairV3[] = [];
  for (const seat of ['a', 'b'] as const) {
    chairs.push({
      participantId: pvpDuelSeatToParticipantId(seat),
      team: seat,
      slot: 1,
      claimed: taken.has(seat),
      accountId: null,
      generation: 0,
      connectionId: null,
      /*
       * A version 2 record did not carry readiness either - it lived on the
       * CONNECTION, and a rebuilt room has none. A running room's sockets
       * declare themselves ready again on reattach, exactly as they did before
       * this format existed; a waiting room hears `ready` from the client,
       * which repeats it on every reconnection.
       */
      ready: false,
      disconnectedSince: dropped.get(seat) ?? null,
      // A version 2 room had no voluntary-exit intent to record: the button
      // did not reach the server at all. Nobody in one had abandoned.
      leftAt: null,
      appearance: appearance.get(seat) ?? null,
      /*
       * A version 2 room announced per MATCH and never per participant, and
       * its `announced` flag below carries that. Nothing is marked here: a duel
       * that ended has `announced` set, and a duel still running has never had
       * an elimination to announce, because in a duel an elimination IS the
       * ending.
       */
      abandonAnnounced: false,
      eliminationAnnounced: false,
    });
  }
  return {
    v: PVP_ROOM_STATE_V3_VERSION,
    matchId: persisted.matchId,
    mode: persisted.mode,
    ruleset: PVP_DUEL_RULESET,
    state: persisted.state,
    chairs,
    simulation: { kind: 'duel_v2', snapshot: persisted.snapshot },
    accumulatorMs: persisted.accumulatorMs,
    lastPumpAt: persisted.lastPumpAt,
    /*
     * Version 2 did not persist the broadcast cadence and did not need to: it
     * is presentation, it only ever suppresses a duplicate frame inside one
     * tick, and starting from zero costs at most one extra frame.
     */
    lastBroadcastAt: 0,
    /*
     * A room that already reached `running` has already had its start
     * announced - version 2's reader derived exactly this from the state, and
     * it is written down here rather than re-derived in three places.
     */
    startAnnounced: persisted.state === 'running' || persisted.state === 'completed',
    announced: persisted.announced,
  };
}

/**
 * Reads a persisted room of either version.
 *
 * The version is read from the record's own stamp and from nothing else - no
 * sniffing, no "does it have chairs", no heuristic. A stamp this build does not
 * know is refused rather than guessed at, and a document carrying both
 * vocabularies is refused as a splice rather than resolved in somebody's favour.
 */
export function pvpReadRoomState(value: unknown): PvpRoomStateV3 {
  const raw = record(value, 'pvp room state');
  const version = raw.v;
  if (version === PVP_ROOM_STATE_V3_VERSION) {
    return pvpDecodeRoomStateV3(raw);
  }
  if (version === PVP_ROOM_STATE_VERSION) {
    if ('chairs' in raw || 'ruleset' in raw || 'simulation' in raw) {
      refuse('hybrid_document', 'a version 2 room state carrying version 3 fields is neither');
    }
    return pvpUpgradeRoomStateV2(raw as unknown as PvpRoomPersistedState);
  }
  refuse('unknown_version', `unknown pvp room state version: ${String(version)}`);
}

/** A chair as it starts life: unclaimed, unconnected, and never yet announced. */
export function pvpEmptyChair(
  participantId: PvpParticipantId,
  team: 'a' | 'b',
  slot: PvpTeamSlot,
): PvpRoomChairV3 {
  return {
    participantId,
    team,
    slot,
    claimed: false,
    accountId: null,
    generation: 0,
    connectionId: null,
    ready: false,
    disconnectedSince: null,
    leftAt: null,
    appearance: null,
    abandonAnnounced: false,
    eliminationAnnounced: false,
  };
}

/** Every chair a ruleset has, empty, in canonical order. */
export function pvpEmptyChairs(ruleset: PvpRuleset): readonly PvpRoomChairV3[] {
  return pvpCanonicalSeats(ruleset).map((seat) =>
    pvpEmptyChair(pvpParticipantId(seat.team, seat.slot), seat.team, seat.slot),
  );
}
